/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo } from "react";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { BEM, FONT_SIZE, someIncludesCaseInsensitive } from "@adapptio/lib-components";
import { Select } from "../Select/Select";
import { SelectItem } from "../Select/SelectItem";
import { SelectItemText } from "../Select/SelectItemText";
import { SelectEmpty } from "../Select/SelectEmpty";
import { Typography } from "../Typography";
import { ISchemaStringWithConstConstItem } from "@adapptio/lib-blueprint";
import { InputAdornment } from "../InputAdornment";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldFontSizeProps {
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
	constants?: ISchemaStringWithConstConstItem[];
}

const type = "font-size";
const fieldClassName = `edt-field--type-${type}`;

export const FieldFontSize: FC<IFieldFontSizeProps> = ({
	value,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange,
	constants
}) => {
	const baseClass = BEM("edt-field-control");
	const fieldClass = BEM(fieldClassName);

	const options = constants || [];

	const filteredOptions = useMemo(
		() =>
			value && !options.some((option) => option.value === value)
				? options.filter((option) => someIncludesCaseInsensitive(value, [ option.value ]))
				: options,
		[ value, constants ]
	);

	const getOptionSize = useCallback<(option: ISchemaStringWithConstConstItem) => string>(
		(option) => FONT_SIZE[option.value] || option.value,
		[]
	);

	return (
		<FieldWrapper type={type} className={className} grow={grow}>
			<Select
				value={value}
				className={baseClass.toClassName()}
				placeholder={placeholder}
				popupClassName={fieldClass.element("popup").toClassName()}
				onChange={onChange}
				allowCustomInput
				readOnly={readOnly}
				endAdornment={
					validationErrors?.length > 0 && (
						<InputAdornment>
							<FieldValidationErrors errors={validationErrors} />{" "}
						</InputAdornment>
					)
				}
			>
				{filteredOptions?.length > 0 ? (
					filteredOptions.map((option) => (
						<SelectItem
							key={option.value}
							value={option.value}
							className={fieldClass.element("option").toClassName()}
						>
							<SelectItemText
								primary={
									<Typography
										size={FONT_SIZE.XSMALL}
										className={fieldClass.element("option-label").toClassName()}
									>
										{option.label}
									</Typography>
								}
								secondary={
									<Typography
										size={getOptionSize(option)}
										className={fieldClass.element("option-preview").toClassName()}
										preferCssValue
										noWrap
										truncate
									>
										Lorem ipsum dolor sit amet
									</Typography>
								}
							/>
						</SelectItem>
					))
				) : (
					<SelectEmpty>No items found</SelectEmpty>
				)}
			</Select>
		</FieldWrapper>
	);
};
